import React, { createContext, useContext, useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';

export const PlanContext = createContext();

export const usePlan = () => {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('usePlan must be used within a PlanProvider');
  }
  return context;
};

export const PlanProvider = ({ children }) => {
  const [userPlan, setUserPlan] = useState(null); // null = not authenticated
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [planLimits, setPlanLimits] = useState({
    max_opportunities_visible: 1,
    can_view_roi: false,
    can_view_roadmap: false,
    can_export_pdf: false,
    can_view_full_details: false,
    max_historical_scans: 0,
    has_team_access: false,
    is_consultant: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserPlan();
  }, []);

  const fetchUserPlan = async () => {
    try {
      console.log('[PlanContext] Fetching user plan from:', `${BACKEND_URL}/api/auth/me`);
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        // Plan data received successfully
        setUserPlan(data.plan || 'starter');
        setPlanLimits(data.plan_limits || planLimits);
        setIsAuthenticated(true);
      } else {
        // Not authenticated
        setUserPlan(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Error fetching user plan:', error);
      setUserPlan(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const checkFeatureAccess = (feature) => {
    return planLimits[feature] === true;
  };

  // Plan tier check helpers - support both naming conventions
  const isFreePlan = () => userPlan === 'starter' || userPlan === 'basic' || userPlan === 'free';
  const isProPlan = () => userPlan === 'pro' || userPlan === 'teams';
  const isPartnerPlan = () => userPlan === 'partner_pro' || userPlan === 'partner-pro';
  
  // Helper to check if user has Pro-level access or higher
  const isProOrHigher = () => {
    return userPlan === 'pro' || userPlan === 'teams' || userPlan === 'partner_pro' || userPlan === 'partner-pro' || userPlan === 'enterprise';
  };

  // Helper to check if user has Builder-level access or higher
  const isBuilderOrHigher = () => {
    return userPlan === 'builder' || userPlan === 'professional' || userPlan === 'pro' || userPlan === 'teams' || userPlan === 'partner_pro' || userPlan === 'partner-pro' || userPlan === 'enterprise';
  };

  const value = {
    plan: userPlan, // Alias for convenience
    tier: userPlan, // Alias for components expecting 'tier'
    userPlan,
    isAuthenticated,
    planLimits,
    loading,
    checkFeatureAccess,
    isFreePlan,
    isProPlan,
    isPartnerPlan,
    isProOrHigher,
    isBuilderOrHigher,
    refreshPlan: fetchUserPlan
  };

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
};
